import type { Routes } from "@angular/router";
import { authGuard, publicGuard } from "./guards/auth.guard";
import { seriesRoutes } from "./pages/series/series.routes";

/**
 * Application route paths.
 * Use these constants instead of magic strings to prevent typos and improve refactoring.
 */
export const AppRoutes = {
	Home: "home",
	Auth: "auth",
	AuthCallback: "auth/callback",
	Dashes: "dashes",
	DashDetail: (id: string) => `dashes/${id}`,
	Series: "series",
	SeriesDetail: (id: string) => `series/${id}`,
	Users: "users",
	UserDetail: (id: string) => `users/${id}`,
	Purchases: "purchases",
	Marking: "marking",
	MarkingFeed: "marking/feed",
	MarkingSession: (id: string) => `marking/session/${id}`,
	Teams: "teams",
	TeamDetail: (id: string) => `teams/${id}`,
	Photos: "photos",
} as const;

export const appRoutes: Routes = [
	{
		path: "",
		redirectTo: AppRoutes.Auth,
		pathMatch: "full",
	},
	{
		path: AppRoutes.Auth,
		children: [
			{
				path: "",
				loadComponent: () =>
					import("./pages/auth/auth-page.component").then(
						(m) => m.AuthPageComponent,
					),
				canActivate: [publicGuard],
			},
			{
				path: "callback",
				loadComponent: () =>
					import("@magpie/ui").then((m) => m.AuthCallbackComponent),
			},
		],
	},
	{
		path: "",
		loadComponent: () =>
			import("./shell/shell.component").then((m) => m.ShellComponent),
		canActivate: [authGuard],
		children: [
			{
				path: AppRoutes.Home,
				loadComponent: () =>
					import("./pages/home/home.component").then((m) => m.HomeComponent),
			},
			{
				path: AppRoutes.Dashes,
				loadComponent: () =>
					import("./pages/dash-list/dash-list.component").then(
						(m) => m.DashListComponent,
					),
			},
			{
				path: "dashes/:id",
				loadComponent: () =>
					import("./pages/dash-edit/dash-edit.component").then(
						(m) => m.DashEditComponent,
					),
			},
			{
				path: AppRoutes.Users,
				loadComponent: () =>
					import("./pages/users/users.component").then((m) => m.UsersComponent),
			},
			{
				path: "users/:id",
				loadComponent: () =>
					import("./pages/users/user-detail.page").then(
						(m) => m.UserDetailPage,
					),
			},
			{
				path: AppRoutes.Series,
				children: seriesRoutes,
			},
			{
				path: AppRoutes.Purchases,
				loadComponent: () =>
					import("./pages/purchases/purchases.component").then(
						(m) => m.PurchasesComponent,
					),
			},
			{
				path: AppRoutes.Marking,
				loadComponent: () =>
					import("./pages/marking/marking-queue.page").then(
						(m) => m.MarkingQueuePage,
					),
			},
			{
				path: "marking/feed",
				loadComponent: () =>
					import("./pages/marking/marking-feed.page").then(
						(m) => m.MarkingFeedPage,
					),
			},
			{
				path: "marking/session/:id",
				loadComponent: () =>
					import("./pages/marking/marking-session.page").then(
						(m) => m.MarkingSessionPage,
					),
			},
			{
				path: AppRoutes.Teams,
				loadComponent: () =>
					import("./pages/teams/teams-list.page").then((m) => m.TeamsListPage),
			},
			{
				path: AppRoutes.Photos,
				loadComponent: () =>
					import("./pages/photos/photos.page").then((m) => m.PhotosPage),
			},
			{
				path: "teams/:id",
				loadComponent: () =>
					import("./pages/teams/team-edit.page").then((m) => m.TeamEditPage),
			},
		],
	},
];
